import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  Wallet, FileWarning, Clock, TrendingUp, ArrowUpRight, 
  Zap, CreditCard, Calendar, ChevronRight
} from 'lucide-react';
import { Card } from '@/components/ui/card';
import { translateProvider } from '@/utils/providerTranslations';
import { Button } from '@/components/ui/button';
import BillCard from '@/components/BillCard';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Mini donut chart component
const MiniDonutChart = ({ percentage, color, size = 60 }) => {
  const circumference = 2 * Math.PI * 20;
  const strokeDashoffset = circumference - (percentage / 100) * circumference;
  
  return (
    <svg width={size} height={size} className="transform -rotate-90">
      <circle
        cx={size/2}
        cy={size/2}
        r="20"
        fill="none"
        stroke="#e2e8f0"
        strokeWidth="6"
      />
      <circle
        cx={size/2}
        cy={size/2}
        r="20"
        fill="none"
        stroke={color}
        strokeWidth="6"
        strokeLinecap="round"
        strokeDasharray={circumference}
        strokeDashoffset={strokeDashoffset}
        style={{ transition: 'stroke-dashoffset 1s ease-out' }}
      />
    </svg>
  );
};

// Category breakdown chart
const CategoryChart = ({ categories }) => {
  const colors = {
    water: '#3b82f6',
    electricity: '#f59e0b', 
    arnona: '#8b5cf6',
    fines: '#ef4444',
    education: '#10b981',
    health: '#ec4899',
    general: '#6366f1',
    vaad_bayit: '#14b8a6'
  };

  const total = categories.reduce((sum, cat) => sum + cat.amount, 0);

  return (
    <div className="space-y-3">
      {categories.map((cat, idx) => {
        const percentage = total > 0 ? (cat.amount / total) * 100 : 0;
        return (
          <div key={idx} className="space-y-1">
            <div className="flex justify-between text-sm">
              <span className="text-slate-600 capitalize">{cat.name}</span>
              <span className="font-semibold text-slate-900">₪{cat.amount.toFixed(0)}</span>
            </div>
            <div className="progress-bar">
              <div 
                className="progress-fill"
                style={{ 
                  width: `${percentage}%`,
                  background: colors[cat.key] || '#6366f1'
                }}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default function Dashboard({ token }) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [bills, setBills] = useState([]);
  const [loading, setLoading] = useState(true);
  const [animatedTotal, setAnimatedTotal] = useState(0);

  useEffect(() => {
    fetchBills();
  }, []);

  const fetchBills = async () => {
    try {
      const response = await axios.get(`${API}/bills`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBills(response.data);
    } catch (error) {
      toast.error('Failed to load bills');
    } finally {
      setLoading(false);
    }
  };

  const unpaidBills = bills.filter((b) => b.status === 'unpaid');
  const totalDebt = unpaidBills.reduce((sum, b) => sum + b.amount, 0);
  const paidBills = bills.filter((b) => b.status === 'paid');
  const totalPaid = paidBills.reduce((sum, b) => sum + b.amount, 0);
  
  const urgentBills = unpaidBills.filter((b) => {
    const daysUntilDue = Math.ceil((new Date(b.due_date) - new Date()) / (1000 * 60 * 60 * 24));
    return daysUntilDue <= 7;
  });

  // Animate total debt
  useEffect(() => {
    if (totalDebt > 0) {
      const duration = 1500;
      const steps = 60;
      const increment = totalDebt / steps;
      let current = 0;
      const timer = setInterval(() => {
        current += increment;
        if (current >= totalDebt) {
          setAnimatedTotal(totalDebt);
          clearInterval(timer);
        } else {
          setAnimatedTotal(current);
        }
      }, duration / steps);
      return () => clearInterval(timer);
    }
  }, [totalDebt]);

  // Calculate categories
  const categoryData = useMemo(() => {
    const cats = {};
    unpaidBills.forEach(bill => {
      if (!cats[bill.category]) {
        cats[bill.category] = { key: bill.category, name: t(bill.category), amount: 0 };
      }
      cats[bill.category].amount += bill.amount;
    });
    return Object.values(cats).sort((a, b) => b.amount - a.amount).slice(0, 5);
  }, [unpaidBills, t]);

  // Payment progress percentage
  const paymentProgress = bills.length > 0 
    ? Math.round((paidBills.length / bills.length) * 100) 
    : 0;

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <div className="relative">
            <div className="w-16 h-16 border-4 border-blue-200 rounded-full animate-spin border-t-blue-600 mx-auto"></div>
          </div>
          <p className="text-slate-600 mt-4 font-medium">{t('loading')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto space-y-8 pb-24 lg:pb-8">
      {/* Hero Header */}
      <div className="animate-fadeIn space-y-2">
        <h1 className="text-4xl md:text-5xl font-extrabold text-slate-900 tracking-tight" data-testid="dashboard-title">
          {t('dashboard')}
        </h1>
      </div>

      {/* Main Stats - Premium Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Total Debt Card */}
        <div className="stat-card animate-fadeInUp stagger-1 col-span-1 md:col-span-2" data-testid="stat-total-debt">
          <div className="flex items-start justify-between">
            <div className="space-y-1">
              <p className="text-sm font-medium text-slate-500 uppercase tracking-wider">{t('total_debt')}</p>
              <p className="text-5xl font-extrabold text-slate-900 number-counter">
                ₪{animatedTotal.toFixed(2)}
              </p>
              <div className="flex items-center gap-2 mt-3">
                <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-amber-100 text-amber-700">
                  <Clock className="h-3 w-3 mr-1" />
                  {urgentBills.length} {t('upcoming_due')}
                </span>
              </div>
            </div>
            <div className="bg-gradient-to-br from-blue-500 to-blue-600 p-4 rounded-2xl shadow-lg shadow-blue-500/30 animate-float">
              <Wallet className="h-8 w-8 text-white" />
            </div>
          </div>
          
          {/* Quick action */}
          <Button 
            onClick={() => navigate('/bills')}
            className="mt-6 w-full bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white rounded-xl h-12 font-semibold shadow-lg shadow-blue-500/25 transition-all hover:shadow-xl hover:shadow-blue-500/30"
            data-testid="pay-all-button"
          >
            <CreditCard className="h-5 w-5 mr-2" />
            {t('pay_now')}
            <ArrowUpRight className="h-4 w-4 ml-2" />
          </Button>
        </div>

        {/* Progress Card */}
        <div className="stat-card animate-fadeInUp stagger-2" data-testid="stat-progress">
          <div className="text-center">
            <div className="relative inline-flex items-center justify-center mb-4">
              <svg className="w-32 h-32 transform -rotate-90">
                <circle
                  cx="64"
                  cy="64"
                  r="56"
                  fill="none"
                  stroke="#e2e8f0"
                  strokeWidth="12"
                />
                <circle
                  cx="64"
                  cy="64"
                  r="56"
                  fill="none"
                  stroke="url(#progressGradient)"
                  strokeWidth="12"
                  strokeLinecap="round"
                  strokeDasharray={2 * Math.PI * 56}
                  strokeDashoffset={2 * Math.PI * 56 * (1 - paymentProgress / 100)}
                  style={{ transition: 'stroke-dashoffset 1.5s ease-out' }}
                />
                <defs>
                  <linearGradient id="progressGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                    <stop offset="0%" stopColor="#10b981" />
                    <stop offset="100%" stopColor="#059669" />
                  </linearGradient>
                </defs>
              </svg>
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="text-center">
                  <span className="text-3xl font-extrabold text-slate-900">{paymentProgress}%</span>
                  <p className="text-xs text-slate-500 font-medium">{t('paid')}</p>
                </div>
              </div>
            </div>
            <p className="text-sm text-slate-600">
              <span className="font-bold text-green-600">{paidBills.length}</span> / {bills.length} {t('bills').toLowerCase()}
            </p>
          </div>
        </div>
      </div>

      {/* Secondary Stats Row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="stat-card animate-fadeInUp stagger-3 p-5" data-testid="stat-unpaid">
          <div className="flex items-center gap-3">
            <div className="bg-amber-100 p-3 rounded-xl">
              <FileWarning className="h-5 w-5 text-amber-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-900">{unpaidBills.length}</p>
              <p className="text-xs text-slate-500">{t('unpaid_bills')}</p>
            </div>
          </div>
        </div>

        <div className="stat-card animate-fadeInUp stagger-4 p-5" data-testid="stat-urgent">
          <div className="flex items-center gap-3">
            <div className="bg-red-100 p-3 rounded-xl">
              <Zap className="h-5 w-5 text-red-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-900">{urgentBills.length}</p>
              <p className="text-xs text-slate-500">{t('upcoming_due')}</p>
            </div>
          </div>
        </div>

        <div className="stat-card animate-fadeInUp stagger-5 p-5" data-testid="stat-paid-amount">
          <div className="flex items-center gap-3">
            <div className="bg-green-100 p-3 rounded-xl">
              <TrendingUp className="h-5 w-5 text-green-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-900">₪{totalPaid.toFixed(0)}</p>
              <p className="text-xs text-slate-500">{t('paid')}</p>
            </div>
          </div>
        </div>

        <div 
          className="stat-card animate-fadeInUp stagger-6 p-5 cursor-pointer hover:bg-blue-50 transition-colors" 
          onClick={() => navigate('/appointments')}
          data-testid="stat-appointments"
        >
          <div className="flex items-center gap-3">
            <div className="bg-blue-100 p-3 rounded-xl">
              <Calendar className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-900">+</p>
              <p className="text-xs text-slate-500">{t('book_appointment')}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Category Breakdown & Urgent Bills */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Category Breakdown */}
        {categoryData.length > 0 && (
          <Card className="premium-card p-6 animate-fadeInUp" data-testid="category-breakdown">
            <h3 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2">
              <div className="w-1 h-5 bg-blue-600 rounded-full"></div>
              {t('category')}
            </h3>
            <CategoryChart categories={categoryData} />
          </Card>
        )}

        {/* Urgent Bills */}
        <Card className="premium-card p-6 lg:col-span-2 animate-fadeInUp" data-testid="urgent-bills-section">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-bold text-slate-900 flex items-center gap-2">
              <div className="w-1 h-5 bg-red-500 rounded-full"></div>
              {t('upcoming_due')}
            </h3>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/bills')}
              className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
            >
              {t('view_details')}
              <ChevronRight className="h-4 w-4 ml-1" />
            </Button>
          </div>
          
          {urgentBills.length > 0 ? (
            <div className="space-y-3">
              {urgentBills.slice(0, 3).map((bill, idx) => {
                const daysLeft = Math.ceil((new Date(bill.due_date) - new Date()) / (1000 * 60 * 60 * 24));
                return (
                  <div 
                    key={bill.id}
                    onClick={() => navigate(`/bills/${bill.id}`)}
                    className="flex items-center justify-between p-4 bg-slate-50 hover:bg-slate-100 rounded-xl cursor-pointer transition-all hover:scale-[1.02]"
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-2 h-2 rounded-full ${daysLeft <= 3 ? 'bg-red-500' : 'bg-amber-500'}`} />
                      <div>
                        <p className="font-semibold text-slate-900">{translateProvider(bill.provider, i18n.language)}</p>
                        <p className="text-sm text-slate-500">{t(bill.category)}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-bold text-slate-900">₪{bill.amount.toFixed(2)}</p>
                      <p className={`text-xs font-medium ${daysLeft <= 3 ? 'text-red-600' : 'text-amber-600'}`}>
                        {daysLeft} {daysLeft === 1 ? t('day_left') : t('days_left')}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-8">
              <div className="bg-green-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-3">
                <TrendingUp className="h-8 w-8 text-green-600" />
              </div>
              <p className="text-slate-600">{t('no_urgent_bills')}</p>
            </div>
          )}
        </Card>
      </div>

      {/* All Unpaid Bills */}
      {unpaidBills.length > 0 && (
        <div className="space-y-4 animate-fadeInUp">
          <div className="flex items-center justify-between">
            <h2 className="text-2xl font-bold text-slate-900 flex items-center gap-3">
              <div className="w-1 h-6 bg-blue-600 rounded-full"></div>
              {t('unpaid_bills')}
            </h2>
            <Button
              variant="ghost"
              onClick={() => navigate('/bills')}
              className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
              data-testid="view-all-bills-button"
            >
              {t('view_details')} 
              <ChevronRight className="h-4 w-4 ml-1" />
            </Button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {unpaidBills.slice(0, 6).map((bill, idx) => (
              <div key={bill.id} className={`animate-fadeInUp stagger-${Math.min(idx + 1, 6)}`}>
                <BillCard bill={bill} onClick={() => navigate(`/bills/${bill.id}`)} />
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Empty State */}
      {unpaidBills.length === 0 && (
        <Card className="premium-card p-12 text-center animate-scaleIn" data-testid="no-bills-message">
          <div className="bg-gradient-to-br from-green-400 to-green-500 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg shadow-green-500/30 animate-pulse-slow">
            <TrendingUp className="h-10 w-10 text-white" />
          </div>
          <h3 className="text-2xl font-bold text-slate-900 mb-2">{t('all_caught_up')}</h3>
          <p className="text-slate-600 mb-6">{t('no_unpaid_bills')}</p>
          <Button
            onClick={() => navigate('/history')}
            variant="outline"
            className="rounded-xl"
          >
            {t('view_details')} {t('history')}
          </Button>
        </Card>
      )}
    </div>
  );
}
